import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { modKey } from '@/shared/utils/modKey';

export interface ShortcutSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Every key the application binds (spec 077 FR-018, FR-019).
 *
 * This list is the reader-facing half of
 * `specs/077-command-palette/contracts/shortcuts.md`, and the two must be edited
 * together: a shortcut absent from here is one nobody can discover, which is the whole
 * reason the requirement exists.
 *
 * It is also the first surface ever to make the workspace task list's `/` findable. That
 * binding has existed since spec 068 and has never been documented anywhere a reader
 * would look.
 */
const MOD = modKey();

const SHORTCUTS: { keys: string; does: string; where: string }[] = [
  // The platform's own modifier (spec 129 FR-012).
  { keys: `${MOD}+K`, does: 'Open the command palette', where: 'Anywhere' },
  { keys: 'Esc', does: 'Close the palette', where: 'Palette open' },
  { keys: '?', does: 'Show this list', where: 'Anywhere' },
  { keys: '/', does: 'Focus the search field', where: 'Workspace tasks' },
  { keys: 'j / k', does: 'Next / previous task', where: 'Workspace tasks' },
  { keys: `${MOD}+Z`, does: 'Undo', where: 'Workflow builder' },
  {
    keys: MOD === '⌘' ? '⌘⇧Z' : 'Ctrl+Y',
    does: 'Redo',
    where: 'Workflow builder',
  },
  // Every key the app handles is listed (spec 129 FR-013).
  {
    keys: 'Arrow keys',
    does: 'Nudge the selected step',
    where: 'Workflow builder',
  },
  {
    keys: 'Delete',
    does: 'Remove the selected step',
    where: 'Workflow builder',
  },
  { keys: 'Arrow keys', does: 'Move between steps', where: 'Workflow diagram' },
  { keys: 'Esc', does: 'Clear the selected step', where: 'Workflow diagram' },
  { keys: '/', does: 'Focus the search field', where: 'Library' },
  { keys: 'Esc', does: 'Close the preview', where: 'Files' },
  // Spec 098: the board answers the task list's keys, and a few of its own.
  { keys: 'j / k', does: 'Next / previous card in the column', where: 'Board' },
  { keys: 'h / l', does: 'Nearest card in the next column', where: 'Board' },
  { keys: 'Enter', does: 'Open the focused card', where: 'Board' },
  {
    keys: 'Shift+Enter',
    does: 'Do the task’s next thing — Start, Stop, Run again, or choose an outcome',
    where: 'Board',
  },
  { keys: 'x', does: 'Select or deselect the focused card', where: 'Board' },
  { keys: 'Esc', does: 'Clear the selection', where: 'Board' },
  { keys: 'u', does: 'Undo the last move, while offered', where: 'Board' },
  // Spec 145: the markdown editor's own keys, while its text has focus. ⌘K
  // there adds a link rather than opening the palette.
  { keys: `${MOD}+B`, does: 'Bold', where: 'In a text editor' },
  { keys: `${MOD}+I`, does: 'Italic', where: 'In a text editor' },
  { keys: `${MOD}+K`, does: 'Add a link', where: 'In a text editor' },
];

export function ShortcutSheet({ open, onOpenChange }: ShortcutSheetProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Never taller than the screen: the header stays put and the list
          scrolls under it, its column names pinned. It used to grow past the
          bottom edge with nothing to scroll. Wider than the default too, so
          fewer rows wrap. */}
      <DialogContent className="flex max-h-[min(85dvh,44rem)] flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            What this application understands from the keyboard.
          </DialogDescription>
        </DialogHeader>
        {/* Scrolls both ways: three columns of shortcut, meaning and scope do
            not fit a phone, and the list is longer than a short screen. */}
        <div
          data-testid="shortcut-list"
          className="-mx-4 min-h-0 flex-1 overflow-auto px-4"
        >
          <table className="w-full text-sm">
            <caption className="sr-only">
              Every keyboard shortcut, what it does, and where it applies
            </caption>
            <thead>
              <tr className="text-muted-foreground border-border border-b text-xs">
                <th
                  scope="col"
                  className="bg-popover sticky top-0 py-1.5 text-left font-medium"
                >
                  Key
                </th>
                <th
                  scope="col"
                  className="bg-popover sticky top-0 py-1.5 text-left font-medium"
                >
                  Does
                </th>
                <th
                  scope="col"
                  className="bg-popover sticky top-0 py-1.5 text-left font-medium"
                >
                  Where
                </th>
              </tr>
            </thead>
            <tbody>
              {SHORTCUTS.map((shortcut) => (
                <tr
                  // Keys repeat across places (Esc, /, j / k, ⌘K), so the
                  // place is part of what makes a row unique.
                  key={`${shortcut.keys}::${shortcut.where}`}
                  className="border-border border-b last:border-b-0"
                >
                  <th
                    scope="row"
                    className="py-1.5 pe-4 text-left font-mono text-xs font-normal whitespace-nowrap"
                  >
                    {shortcut.keys}
                  </th>
                  <td className="py-1.5 pe-4">{shortcut.does}</td>
                  <td className="text-muted-foreground py-1.5 text-xs">
                    {shortcut.where}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
